import { AssetType } from './asset-type.enum';

export interface AssetRelationship {
  id: string;
  name: string;
  entityType: string;
  relationshipType: string;
}

/**
 * Asset metadata
 */
export interface AssetMetadata {
  description?: string;
  owner?: string;
  createdAt?: Date;
  updatedAt?: Date;
  tags?: string[];
  properties?: Record<string, unknown>;
  relationships?: AssetRelationship[];
}

/**
 * Asset entity representing a refinery asset in the ontology
 * Core business entity with identity and behavior
 */
export class Asset {
  private _isExpanded = false;
  private _isSelected = false;
  private _isLoading = false;

  constructor(
    private readonly _id: string,
    private _name: string,
    private readonly _type: AssetType,
    private readonly _entityType: string,
    private readonly _parentId: string | null,
    private _children: Asset[],
    private readonly _metadata: AssetMetadata = {},
  ) {}

  // Getters
  get id(): string {
    return this._id;
  }

  get name(): string {
    return this._name;
  }

  get type(): AssetType {
    return this._type;
  }

  get entityType(): string {
    return this._entityType;
  }

  get parentId(): string | null {
    return this._parentId;
  }

  get children(): ReadonlyArray<Asset> {
    return this._children;
  }

  get metadata(): Readonly<AssetMetadata> {
    return this._metadata;
  }

  get isExpanded(): boolean {
    return this._isExpanded;
  }

  get isSelected(): boolean {
    return this._isSelected;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  // Business methods

  /**
   * Check if this asset has children
   */
  hasChildren(): boolean {
    return this._children.length > 0;
  }

  /**
   * Check if this asset is a leaf node
   */
  isLeaf(): boolean {
    return !this.hasChildren();
  }

  /**
   * Check if this asset can be expanded
   * Can expand if it has children and is not already expanded
   */
  canExpand(): boolean {
    return this.hasChildren() && !this._isExpanded;
  }

  /**
   * Check if this asset can be collapsed
   * Can collapse if it is currently expanded
   */
  canCollapse(): boolean {
    return this._isExpanded;
  }

  /**
   * Expand this asset to show children
   */
  expand(): void {
    if (!this.canExpand()) {
      throw new Error(
        `Cannot expand asset ${this._id}: already expanded or has no children`,
      );
    }
    this._isExpanded = true;
  }

  /**
   * Collapse this asset to hide children
   */
  collapse(): void {
    if (!this.canCollapse()) {
      throw new Error(`Cannot collapse asset ${this._id}: not expanded`);
    }
    this._isExpanded = false;
  }

  /**
   * Toggle expansion state
   */
  toggleExpansion(): void {
    this._isExpanded = !this._isExpanded;
  }

  /**
   * Select this asset
   */
  select(): void {
    this._isSelected = true;
  }

  /**
   * Deselect this asset
   */
  deselect(): void {
    this._isSelected = false;
  }

  /**
   * Mark this asset as loading (for lazy loading)
   */
  startLoading(): void {
    this._isLoading = true;
  }

  /**
   * Mark this asset as done loading
   */
  stopLoading(): void {
    this._isLoading = false;
  }

  updateProperty(propertyName: string, value: unknown): void {
    if (propertyName === 'object_name' || propertyName === 'name') {
      this._name = String(value);
    }
    if (this._metadata.properties) {
      (this._metadata as AssetMetadata).properties![propertyName] = value;
    }
  }

  /**
   * Add a child asset
   */
  addChild(child: Asset): void {
    if (child.parentId !== this._id) {
      throw new Error(
        `Cannot add child with different parentId: expected ${this._id}, got ${child.parentId}`,
      );
    }
    this._children.push(child);
  }

  /**
   * Add multiple children
   */
  addChildren(children: Asset[]): void {
    children.forEach((child) => this.addChild(child));
  }

  /**
   * Clear all children (used before rebuilding hierarchy from flat list)
   */
  clearChildren(): void {
    this._children = [];
  }

  /**
   * Remove a child asset by ID
   */
  removeChild(childId: string): boolean {
    const initialLength = this._children.length;
    this._children = this._children.filter((child) => child.id !== childId);
    return this._children.length < initialLength;
  }

  /**
   * Find a child by ID
   */
  findChild(childId: string): Asset | undefined {
    return this._children.find((child) => child.id === childId);
  }

  /**
   * Get depth level (0 for root)
   */
  getDepth(hierarchy: Asset[]): number {
    let depth = 0;
    let currentParentId = this._parentId;

    while (currentParentId) {
      const parent = hierarchy.find((a) => a.id === currentParentId);
      if (!parent) break;
      depth++;
      currentParentId = parent.parentId;
    }

    return depth;
  }

  /**
   * Check if this asset is a root asset (no parent)
   */
  isRoot(): boolean {
    return this._parentId === null;
  }
}
